import { useAuth } from "../../context/studentAuthContext";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Verified as VerifiedIcon } from "@mui/icons-material";
import { navigateToPeerMate } from "../../utils/peermateHandoff";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function DashboardHome() {
  const {student}=useAuth();

  const [learningStats, setLearningStats] = useState({
    courses: 0,
    sessions: 0,
    groupLearning: 0,
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLearningStats = async () => {
      if (!student?.id) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(
          `${BASE_URL}/api/students/dashboard-stats/${student.id}`
        );

        setLearningStats({
          courses: Number(res.data?.courses || 0),
          sessions: Number(res.data?.sessions || 0),
          groupLearning: Number(res.data?.groupLearning || 0),
        });
      } catch (error) {
        console.error("Error fetching learning stats:", error);
        setLearningStats({
          courses: 0,
          sessions: 0,
          groupLearning: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    // Fetch subscription status
    const fetchSubscriptionStatus = async () => {
      if (!student?.id) return;
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/students/subscriptions/me/${student.id}`
        );
        setSubscriptionStatus(res.data?.isActive ? 'active' : 'inactive');
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        setSubscriptionStatus('inactive');
      }
    };

    const fetchData = async () => {
      await Promise.all([fetchLearningStats(), fetchSubscriptionStatus()]);
    };

    fetchData();
  }, [student?.id]);

  return (
    <div className="header">
    <div className="min-h-screen     ">
      {/* Navbar */}
     

      {/* Welcome Section */}
      <div className=" m-3  md:m-6 p-4 md:p-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-lg md:text-xl font-semibold">
            Welcome back, <span className="text-[#1976D2]">{student?.name}</span>
          </h1>
          {subscriptionStatus === 'active' && (
            <div className="flex items-center gap-1 bg-blue-50 px-3 py-1 rounded-full">
              <VerifiedIcon sx={{ fontSize: 18, color: '#1976D2' }} />
              <span className="text-xs font-semibold text-[#1976D2]">
                Peermate Subscriber
              </span>
            </div>
          )}
        </div>
        <p className="text-sm md:text-base text-gray-500 mb-1">
          Here's what's happening with your learning journey
        </p>
        <p className="text-sm md:text-base text-gray-500">
          Start by joining a class or exploring tutors that fit your interests.
        </p>
      </div>

      {/* PeerMate AI Spotlight Banner */}
      <div className="m-3 md:m-6 p-6 rounded-2xl bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10 max-w-2xl space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
            <span>✨</span>
            <span>AI Adaptive Learning Engine</span>
          </div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight">
            Meet PeerMate AI: Your 24/7 Personal Study Companion
          </h2>
          <p className="text-sm text-blue-200/80 leading-relaxed">
            Curate custom 16-stage learning blueprints, get instant answers with voice tutoring, and practice standardized past questions (JAMB, WAEC, SAT, Tech).
          </p>
          <div className="pt-2 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigateToPeerMate()}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-blue-600/30 flex items-center gap-2"
            >
              <span>Launch PeerMate AI</span>
              <span>→</span>
            </button>
            <Link
              to="/Certification"
              className="px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-xl text-xs font-bold transition border border-white/10"
            >
              My Certifications
            </Link>
          </div>
        </div>
      </div>

      {/* Stats + Action Buttons */}
      <div className="m-3 md:m-6 bg-white rounded-lg shadow p-5">
        {loading ? (
          <p className="text-gray-400 text-sm">Loading your learning stats...</p>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            
            {/* Stats */}
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg w-full">
                <h2 className="text-2xl font-bold text-[#1976D2]">
                  {learningStats.courses}
                </h2>
                <p className="text-gray-500 text-sm">Courses Enrolled</p>
              </div>

              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg w-full">
                <h2 className="text-2xl font-bold text-[#1976D2]">
                  {learningStats.sessions}
                </h2>
                <p className="text-gray-500 text-sm">Upcoming Sessions</p>
              </div>

              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg w-full">
                <h2 className="text-2xl font-bold text-[#1976D2]">
                  {learningStats.groupLearning}
                </h2>
                <p className="text-gray-500 text-sm">Group Learning</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-[40%]">
              <button className="bg-gray-50 text-[#1976D2] rounded-lg shadow px-4 py-3 text-sm hover:bg-gray-100 transition w-full">
              <Link to="/group-classes">
                Browse Classes
                </Link>
              </button>
              <button className="bg-gray-50 text-[#1976D2] rounded-lg shadow px-4 py-3 text-sm hover:bg-gray-100 transition w-full">
                 <Link to="/tutors">
                Find Tutor
                </Link>
              </button>
              {/* <button className="bg-gray-50 text-[#1976D2] rounded-lg shadow px-4 py-3 text-sm hover:bg-gray-100 transition w-full">
                Onsite Cohubs
              </button> */}
            </div>
          </div>
        )}
      </div>

      {/* Group Classes & Utilities */}
      <div className="m-3 md:m-6 flex flex-col md:flex-row gap-6">
        <div className="bg-white rounded-xl shadow p-6 w-full md:w-1/2">
          <h2 className="text-lg font-semibold mb-1">Group Classes</h2>
          <p className="text-gray-500 text-sm mb-4">
            Learn alongside others in guided, tutor-led sessions.
          </p>
          <p className="text-sm text-gray-400">
            No group classes joined yet
          </p>
        </div>

        <div className="flex gap-4 w-full md:w-1/2">
         
          <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#1976D2] font-medium py-4 rounded-lg transition">
          <Link to="/schedule">
            View All Schedules
            </Link>
          </button>
          
          <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#1976D2] font-medium py-4 rounded-lg transition">
            <Link to="/assignments">
            View All Assignments
            </Link>
          </button>
        </div>
      </div>

      {/* Learning Plan */}
      <div className="m-3 md:m-6 bg-white rounded-xl shadow p-6 max-w-xl mb-12">
        <h2 className="text-lg font-semibold mb-1">Your Learning Plan</h2>
        <p className="text-gray-500 text-sm mb-6">
          A simple guide to help you stay on track.
        </p>

        <div className="space-y-5" >
          {[
            "Choose a Course",
            "Pick a Learning Method",
            "Select a Tutor",
            "Confirm & Get Started",
          ].map((step, index) => (
            <div key={index} className="flex gap-3">
              <span className="text-[#1976D2] font-bold">{index + 1}.</span>
              <p className="text-sm text-gray-600">{step}</p>
            </div>
          ))}
        </div>
      </div>
    
    </div>
    </div>
  );
}
